import { Component, Inject, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import {
  DATA_SELECTOR_INJECTION_TOKEN,
  DEFAULT_SNACKBAR_CONFIG,
  DialogSize, Logger,
  mapToSelectable,
  Selectable,
  SnackbarComponent,
  SnackbarType
} from '@aiwyn/shared';

import { ClientRequestDocumentFileCategorize } from '@aiwyn/api';
import { ClientRequestIdentityHelper, RemoveClientRequestFiles } from '@aiwyn/common/client-requests';
import { ConfigService } from '@aiwyn/config';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ClientRequestDocumentFileDisplay, ClientRequestDocumentFileDisplayStatus } from '../../models';
import {
  CategorizeFileIntoDocument,
  ClientRequestsState, UndoFileCategorization
} from '../../state';
import { DocumentRequestModalData } from './document-request-modal.model';

@Component({
  selector: 'app-document-request-modal',
  templateUrl: './document-request-modal.component.html',
  styleUrls: ['./document-request-modal.component.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'document-request-modal',
    },
  ],
  standalone: false,
})
export class DocumentRequestModalComponent implements OnInit, OnDestroy {
  private readonly _destroy$: Subject<void> = new Subject<void>();
  readonly DialogSize = DialogSize;
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);
  protected readonly ClientRequestDocumentFileDisplayStatus = ClientRequestDocumentFileDisplayStatus;
  acceptedFileTypes: string[] = [];

  clientRequestId$ = defer(() => this.data.instance.data$).pipe(map((data) => data?.id));

  jobId$ = defer(() => this.data.instance.groupIdentity$).pipe(
    map((identity) => ClientRequestIdentityHelper.parseGroupIdentity(identity)),
    map((parsedIdentity) => parsedIdentity.id),
  );

  get documentRequestTitle(): string {
    return this.data?.requestDocument?.name || '';
  }

  deletedFilesId: number[] = [];

  selectedFilesId: number[] = [];
  selectedFilesId$: BehaviorSubject<number[]> = new BehaviorSubject([]);
  originallySelectedFilesId$: BehaviorSubject<number[]> = new BehaviorSubject([]);

  allFiles$: Observable<Selectable<ClientRequestDocumentFileDisplay>[]> = combineLatest([
    this._store.select(ClientRequestsState.getAllFileDisplays).pipe(mapToSelectable()),
    this.selectedFilesId$,
  ]).pipe(
    map(([allFiles, selectedFilesId]) => {
      const filesToDisplay = allFiles.filter((file, index) => 
        !(file.data.dmsUploadFileJobId && file.data.dmsId) && allFiles.findIndex(_file => _file.data.id === file.data.id) === index
      );
      
      filesToDisplay.forEach((file) => {
        file.selected = selectedFilesId.some((id) => id === file.data.id);
      });

      return filesToDisplay
    }),
    takeUntil(this._destroy$),
  );

  hasChanges$: Observable<boolean> = combineLatest([this.originallySelectedFilesId$, this.selectedFilesId$]).pipe(
    map(([originallySelectedFilesId, currentlySelectedFilesId]) => {
      if (originallySelectedFilesId.length !== currentlySelectedFilesId.length) {
        return true;
      }

      return !originallySelectedFilesId.every((docId) => currentlySelectedFilesId.includes(docId));
    }),
    takeUntil(this._destroy$),
  );

  constructor(
    private readonly _store: Store,
    private readonly _logger: Logger,
    private readonly _snackbar: MatSnackBar,
    private _dialogRef: MatDialogRef<DocumentRequestModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentRequestModalData,
    private readonly _configService: ConfigService,
  ) {
    this.acceptedFileTypes = this._configService.clientRequestFileTypeWhitelist;
  }

  ngOnInit(): void {
    this.selectedFilesId = this.data.requestDocument.files.map((file) => file.file.id);
    //Must de-struct to avoid referencing the same value in memory
    this.originallySelectedFilesId$.next([...this.selectedFilesId]);
    this.selectedFilesId$.next(this.selectedFilesId);
  }

  clearSelection(): void {
    this.selectedFilesId = [];
    this.selectedFilesId$.next(this.selectedFilesId);
  }

  private formatToCategorize(selectedFile: ClientRequestDocumentFileDisplay): ClientRequestDocumentFileCategorize {
    return {
      fileName: selectedFile.name,
      fileId: selectedFile.id,
      documentId: this.data.requestDocument.id,
    };
  }

  isLoading: boolean = false;

  updateLinks(): void {
    this.isLoading = true;

    combineLatest([this.clientRequestId$, this.allFiles$])
      .pipe(
        take(1),
        map(([clientRequestId, allFiles]): [number, ClientRequestDocumentFileCategorize[], ClientRequestDocumentFileCategorize[]] => {
          const previousSelectedFilesSet = new Set(this.data.requestDocument.files.map((file) => file.file.id));
          const previouslySelectedFiles = this.data.requestDocument.files.map((file) => {
            return {
              fileId: file.file.id,
              documentId: file.documentId,
            };
          });

          const removedFiles = previouslySelectedFiles.filter(
            (file) =>
              !this.selectedFilesId.some((selectedFileId) => selectedFileId === file.fileId) &&
              !this.deletedFilesId.some((selectedFileId) => selectedFileId === file.fileId),
          );
          const filesToCategorize = this.selectedFilesId
            .filter((fileId) => !previousSelectedFilesSet.has(fileId))
            .map((fileId) => {
              const file = allFiles.find((_file) => _file.data.id === fileId);
              return this.formatToCategorize(file.data);
            });

          const filesToUndoCategorization = removedFiles.map(({ fileId }) => {
            const file = allFiles.find((_file) => _file.data.id === fileId);
            return this.formatToCategorize(file.data);
          });

          return [clientRequestId, filesToCategorize, filesToUndoCategorization];
        }),
        switchMap(([clientRequestId, filesToCategorize, filesToUndoCategorization]) => {
          const payloadCategorize = {
            clientRequestId: clientRequestId,
            clientRequestDocumentFileCategorizeList: filesToCategorize,
          };
          const payloadUndo = {
            clientRequestId: clientRequestId,
            clientRequestDocumentFileCategorizeList: filesToUndoCategorization,
          };

          return combineLatest([
            filesToCategorize.length ? this._store.dispatch(new CategorizeFileIntoDocument(payloadCategorize)) : of(undefined),
            filesToUndoCategorization.length ? this._store.dispatch(new UndoFileCategorization(payloadUndo)) : of(undefined),
            of(filesToCategorize),
            of(filesToUndoCategorization),
          ]);
        }),
        switchMap(([, , filesToCategorize, filesToUndoCategorization]) => {
          return combineLatest([this.clientRequestId$, this.allFiles$, of(filesToCategorize), of(filesToUndoCategorization)]);
        }),
        tap((_) => {
          this.isLoading = false;
          this.onClose();
        }),
        catchError((error) => {
          this._logger.error('DocumentRequestModalComponent : updateLinks : error : ', error);
          this._snackbar.openFromComponent(SnackbarComponent, {
            ...DEFAULT_SNACKBAR_CONFIG,
            data: {
              message: 'An error occurred while linking the files, please try again or contact support',
              type: SnackbarType.ERROR,
            },
          });

          this.isLoading = false;
          return of(undefined);
        }),
      )
      .subscribe();
  }

  selectFile(file: Selectable<ClientRequestDocumentFileDisplay>, e?: MouseEvent): void {
    e?.stopPropagation();

    if (this.isMarkedForDeletion(file)) {
      return;
    }

    // No way to identify files
    if (
      file.data.status !== ClientRequestDocumentFileDisplayStatus.UNCATEGORIZED &&
      file.data.status !== ClientRequestDocumentFileDisplayStatus.CATEGORIZED
    ) {
      this._snackbar.openFromComponent(SnackbarComponent, {
        ...DEFAULT_SNACKBAR_CONFIG,
        data: {
          message: 'Wait for the file to upload and run categorization before selecting.',
          type: SnackbarType.INFO,
        },
      });

      return;
    }

    if (file.selected) {
      this.selectedFilesId = this.selectedFilesId.filter((selectedFileId) => selectedFileId !== file.data.id);
    } else {
      this.selectedFilesId.push(file.data.id);
    }

    this.selectedFilesId$.next([...this.selectedFilesId]);
  }

  isMarkedForDeletion(file: Selectable<ClientRequestDocumentFileDisplay>): boolean {
    return this.deletedFilesId.some((id) => id === file.data.id);
  }

  deleteFile(_file: Selectable<ClientRequestDocumentFileDisplay>, e?: MouseEvent): void {
    e?.stopPropagation();
    e?.preventDefault();

    const selectedClientRequestId = this._store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);

    this.deletedFilesId.push(_file.data.id);

    this.allFiles$
      .pipe(
        take(1),
        switchMap((allFiles) => {
          const isAssignedToDocumentRequest = this.data.requestDocument.files.some((file) => {
            return file.file.id === _file.data.id;
          });

          const file = allFiles.find((file) => file.data.id === _file.data.id);

          const payloadUndo = {
            clientRequestId: selectedClientRequestId,
            clientRequestDocumentFileCategorizeList: [this.formatToCategorize(file.data)],
          };

          return isAssignedToDocumentRequest ? this._store.dispatch(new UndoFileCategorization(payloadUndo)) : of(undefined);
        }),
        switchMap(() => this._store.dispatch(new RemoveClientRequestFiles(selectedClientRequestId, [_file.data.id]))),
        take(1),
        tap(() => {
          this.selectedFilesId = this.selectedFilesId.filter((selectedFileId) => selectedFileId !== _file.data.id);
          this.selectedFilesId$.next(this.selectedFilesId);
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  /**
   * Close the modal.
   */
  onClose(): void {
    const stagedJobs = this._store.selectSnapshot(ClientRequestsState.getStagedFileJobs);

    if (stagedJobs.length) {
      this._snackbar.openFromComponent(SnackbarComponent, {
        ...DEFAULT_SNACKBAR_CONFIG,
        data: {
          message: 'Wait for the file to upload and run categorization before closing the modal.',
          type: SnackbarType.INFO,
        },
      });

      return;
    }

    this._dialogRef.close(false);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
